import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import ReactMarkdown from 'react-markdown';
import { ASSET_BASE_URL } from '../config/constants';
import { blogPosts } from './Blog';

// Blog post content
const postContent = {
  'introducing-bonzai-file-visualization': `
Vibe coding is here. AI writes code faster than we can read it—and that's the point. But if we're going to embrace vibe coding fully, we need better tools to keep up with what's being created.

## The Vibe Coding Problem

When you're deep in a vibe coding session with Cursor IDE or using a Claude UI, files are being created and modified at a rapid pace. You lose track of:

- **What files exist** - New files appear, old ones get refactored
- **How they connect** - Dependencies shift as the AI restructures your code
- **What changed** - You still have to review everything, but diffs pile up faster than humanly possible

This is where context rot sets in. Your AI assistant loses track of the codebase. You lose track of the codebase. Tech debt accumulates silently. The vibe dies.

## Context Rot is Real

Every vibe coding session creates entropy. Files get created in the wrong places. Duplicate code appears. The AI doesn't know what it built yesterday. This context rot compounds—and before you know it, you're drowning in tech debt you didn't even write.

Traditional file explorers weren't designed for this pace. The Cursor IDE sidebar shows you files, but not relationships. Claude GUI tools show you conversations, but not code structure. You need something that shows the big picture.

## Enter Bonzai

Bonzai is a real-time file visualization tool built for vibe coding. One command in your terminal:

\`\`\`bash
npx bonzai-tree
\`\`\`

And you get a live, interactive view of your entire codebase. Files appear as nodes. Directories as containers. Changes highlight in real-time as your AI writes code.

## Built for Cursor IDE and Claude UI Workflows

Whether you're using Cursor IDE, Claude's desktop app, or any AI coding assistant, Bonzai runs alongside your workflow:

### Real-Time Updates
As files are created, modified, or deleted, the visualization updates instantly. No refresh needed. Watch your codebase grow in real-time during vibe coding sessions.

### Fight Context Rot
See your entire project structure at a glance. Spot duplicate files. Notice when the AI creates something in the wrong directory. Keep your mental model intact even when you still have to review everything the AI produces.

### Prevent Tech Debt
Catch structural issues before they compound. Bonzai helps you maintain code quality even at vibe coding speeds.

## Getting Started

1. Navigate to your project directory
2. Run \`npx bonzai-tree\`
3. Open the visualization in your browser
4. Start your vibe coding session in Cursor IDE or your preferred Claude UI

The visualization updates as you work, giving you a bird's-eye view of every change your AI makes.

## What's Next

We're just getting started. Coming soon:

- **Dependency graphs** - See how your files import each other
- **Git integration** - Visualize changes across branches
- **AI context sharing** - Help your Claude GUI or Cursor IDE understand file relationships

## Try It Now

Bonzai is free and open source. Give it a spin on your next vibe coding session:

\`\`\`bash
npx bonzai-tree
\`\`\`

Stop drowning in context rot and tech debt. Start vibe coding with clarity.

We'd love to hear what you think. Drop us a note at [sanjog@bonzai.dev](mailto:sanjog@bonzai.dev).

---

*Bonzai - Vibe coding without getting lost.*
`
};

function BlogPost() {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);
  const content = postContent[slug];

  if (!post || !content) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <div style={{
      minHeight: '100vh',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif'
    }}>
      <Helmet>
        <title>{post.title} - Bonzai Blog</title>
        <meta name="description" content={post.description} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.description} />
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={post.date} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.description} />
      </Helmet>

      {/* Header */}
      <header style={{
        padding: '24px',
        borderBottom: '1px solid #eee'
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Link to="/" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            textDecoration: 'none'
          }}>
            <img
              src={`${ASSET_BASE_URL}/favicon.jpg`}
              alt="Bonzai"
              style={{ width: '32px', height: '32px', borderRadius: '6px' }}
            />
            <span style={{ fontSize: '20px', fontWeight: '600', color: '#333' }}>bonzai</span>
          </Link>
          <nav style={{ display: 'flex', gap: '24px' }}>
            <Link to="/visualize" style={{ color: '#666', textDecoration: 'none', fontSize: '14px' }}>Visualize</Link>
            <Link to="/blog" style={{ color: '#666', textDecoration: 'none', fontSize: '14px' }}>Blog</Link>
          </nav>
        </div>
      </header>

      {/* Content */}
      <main style={{
        maxWidth: '700px',
        margin: '0 auto',
        padding: '48px 24px'
      }}>
        <Link
          to="/blog"
          style={{
            color: '#3B82F6',
            textDecoration: 'none',
            fontSize: '14px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            marginBottom: '24px'
          }}
        >
          ← Back to Blog
        </Link>

        <article>
          <header style={{ marginBottom: '32px' }}>
            <h1 style={{
              fontSize: '36px',
              fontWeight: '700',
              color: '#333',
              lineHeight: '1.2',
              marginBottom: '16px'
            }}>
              {post.title}
            </h1>
            <div style={{
              display: 'flex',
              gap: '16px',
              color: '#666',
              fontSize: '14px'
            }}>
              <span>{post.author}</span>
              <span>·</span>
              <time>
                {new Date(post.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </time>
            </div>
          </header>

          {/* Demo Video */}
          <div style={{
            width: '100%',
            marginBottom: '32px',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.1)'
          }}>
            <video
              autoPlay
              loop
              muted
              playsInline
              style={{
                width: '100%',
                display: 'block'
              }}
            >
              <source src={`${ASSET_BASE_URL}/demo.mov`} type="video/quicktime" />
              <source src={`${ASSET_BASE_URL}/demo.mov`} type="video/mp4" />
            </video>
          </div>

          <div className="blog-content" style={{
            fontSize: '18px',
            lineHeight: '1.7',
            color: '#333'
          }}>
            <ReactMarkdown
              components={{
                h2: ({children}) => (
                  <h2 style={{
                    fontSize: '24px',
                    fontWeight: '600',
                    marginTop: '40px',
                    marginBottom: '16px',
                    color: '#333'
                  }}>{children}</h2>
                ),
                h3: ({children}) => (
                  <h3 style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    marginTop: '32px',
                    marginBottom: '12px',
                    color: '#333'
                  }}>{children}</h3>
                ),
                p: ({children}) => (
                  <p style={{
                    marginBottom: '20px',
                    color: '#444'
                  }}>{children}</p>
                ),
                ul: ({children}) => (
                  <ul style={{
                    marginBottom: '20px',
                    paddingLeft: '24px'
                  }}>{children}</ul>
                ),
                li: ({children}) => (
                  <li style={{
                    marginBottom: '8px',
                    color: '#444'
                  }}>{children}</li>
                ),
                code: ({inline, children}) => (
                  inline ? (
                    <code style={{
                      background: '#f3f4f6',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '0.9em',
                      fontFamily: 'Monaco, Consolas, monospace'
                    }}>{children}</code>
                  ) : (
                    <code style={{
                      display: 'block',
                      background: '#1e1e1e',
                      color: '#d4d4d4',
                      padding: '16px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'Monaco, Consolas, monospace',
                      overflowX: 'auto',
                      marginBottom: '20px'
                    }}>{children}</code>
                  )
                ),
                pre: ({children}) => (
                  <pre style={{
                    background: '#1e1e1e',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    overflow: 'hidden'
                  }}>{children}</pre>
                ),
                a: ({href, children}) => (
                  <a href={href} style={{
                    color: '#3B82F6',
                    textDecoration: 'none'
                  }}>{children}</a>
                ),
                hr: () => (
                  <hr style={{
                    border: 'none',
                    borderTop: '1px solid #eee',
                    margin: '40px 0'
                  }} />
                ),
                em: ({children}) => (
                  <em style={{ color: '#666' }}>{children}</em>
                ),
                strong: ({children}) => (
                  <strong style={{ fontWeight: '600' }}>{children}</strong>
                )
              }}
            >
              {content}
            </ReactMarkdown>
          </div>
        </article>
      </main>
    </div>
  );
}

export default BlogPost;
